import React, { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, AlertCircle, AlertOctagon, CheckCircle, Trash2, RefreshCw, Play } from 'lucide-react';
import { apiService, ErrorLog } from '../services/api';
import socketService from '../services/socket';

const levelIcons: Record<ErrorLog['level'], React.ReactNode> = {
    warning: <AlertTriangle size={18} />,
    error: <AlertCircle size={18} />,
    critical: <AlertOctagon size={18} />,
};

const levelColors: Record<ErrorLog['level'], string> = {
    warning: 'var(--warning)',
    error: 'var(--error)',
    critical: '#dc2626',
};

export const ErrorLogsPage: React.FC = () => {
    const [logs, setLogs] = useState<ErrorLog[]>([]);
    const [stats, setStats] = useState<{ total: number; byLevel: Record<string, number>; unresolved: number; last24Hours: number } | null>(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<{ level?: string; resolved?: boolean }>({});
    const [expandedLog, setExpandedLog] = useState<string | null>(null);
    const [triggeringJob, setTriggeringJob] = useState<string | null>(null);

    const loadLogs = useCallback(async () => {
        try {
            setLoading(true);
            const [logsResult, statsResult] = await Promise.all([
                apiService.getErrorLogs({
                    level: filter.level,
                    resolved: filter.resolved,
                    limit: 100,
                }),
                apiService.getErrorLogStats(),
            ]);
            setLogs(logsResult.logs);
            setStats(statsResult);
        } catch (error) {
            console.error('Error loading logs:', error);
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        loadLogs();

        const socket = socketService.connect();
        socket.on('new_error_log', (log: ErrorLog) => {
            setLogs(prev => [log, ...prev]);
            setStats(prevStats => {
                if (!prevStats) return prevStats;
                return {
                    ...prevStats,
                    total: prevStats.total + 1,
                    unresolved: prevStats.unresolved + 1,
                    last24Hours: prevStats.last24Hours + 1,
                    byLevel: {
                        ...prevStats.byLevel,
                        [log.level]: (prevStats.byLevel[log.level] || 0) + 1,
                    },
                };
            });
        });

        return () => {
            socket.off('new_error_log');
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const handleResolve = async (id: string) => {
        try {
            await apiService.resolveErrorLog(id);
            setLogs(prev => prev.map(l => l._id === id ? { ...l, resolved: true, resolvedAt: new Date().toISOString() } : l));
            if (stats) {
                setStats({ ...stats, unresolved: stats.unresolved - 1 });
            }
        } catch (error) {
            console.error('Error resolving log:', error);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this log?')) return;

        try {
            await apiService.deleteErrorLog(id);
            setLogs(prev => prev.filter(l => l._id !== id));
        } catch (error) {
            console.error('Error deleting log:', error);
        }
    };

    const handleTriggerJob = async (jobName: string) => {
        try {
            setTriggeringJob(jobName);
            await apiService.triggerSchedulerJob(jobName);
            alert(`Job '${jobName}' triggered successfully`);
        } catch (error) {
            console.error('Error triggering job:', error);
            alert('Failed to trigger job');
        } finally {
            setTriggeringJob(null);
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'medium' });
    };

    const schedulerJobs = [
        { name: 'check-meeting-reminders', label: 'Meeting Reminders' },
        { name: 'check-follow-up-deadlines', label: 'Follow-up Deadlines' },
        { name: 'check-ignored-leads', label: 'Ignored Leads' },
        { name: 'send-daily-status', label: 'Daily Status' },
    ];

    return (
        <div className="page-grid">
            {/* Stats Cards */}
            <section className="panel stat-grid">
                <article className="stat-chip accent-purple">
                    <div className="stat-icon"><AlertCircle size={22} /></div>
                    <div>
                        <p>Total Logs</p>
                        <strong>{stats?.total || 0}</strong>
                    </div>
                </article>
                <article className="stat-chip accent-orange">
                    <div className="stat-icon"><AlertTriangle size={22} /></div>
                    <div>
                        <p>Unresolved</p>
                        <strong>{stats?.unresolved || 0}</strong>
                    </div>
                </article>
                <article className="stat-chip accent-blue">
                    <div className="stat-icon"><RefreshCw size={22} /></div>
                    <div>
                        <p>Last 24 Hours</p>
                        <strong>{stats?.last24Hours || 0}</strong>
                    </div>
                </article>
                <article className="stat-chip accent-green">
                    <div className="stat-icon"><CheckCircle size={22} /></div>
                    <div>
                        <p>Resolved</p>
                        <strong>{(stats?.total || 0) - (stats?.unresolved || 0)}</strong>
                    </div>
                </article>
            </section>

            {/* Scheduler Controls */}
            <section className="panel">
                <header className="panel-header">
                    <div>
                        <p className="eyebrow">Scheduler</p>
                        <h2>Manual Triggers</h2>
                    </div>
                </header>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {schedulerJobs.map((job) => (
                        <button
                            key={job.name}
                            className="ghost-button"
                            style={{ padding: '0.5rem 1rem' }}
                            disabled={triggeringJob === job.name}
                            onClick={() => handleTriggerJob(job.name)}
                        >
                            <Play size={14} />
                            {triggeringJob === job.name ? 'Running...' : job.label}
                        </button>
                    ))}
                </div>
            </section>

            {/* Error Logs List */}
            <section className="panel span-2">
                <header className="panel-header">
                    <div>
                        <p className="eyebrow">System Logs</p>
                        <h2>
                            <AlertTriangle size={24} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                            Error Logs
                        </h2>
                    </div>
                    <button className="ghost-button" onClick={loadLogs}>
                        <RefreshCw size={16} /> Refresh
                    </button>
                </header>

                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                    <button
                        className={`pill ${filter.level === undefined ? 'highlight' : 'muted'}`}
                        onClick={() => setFilter({ ...filter, level: undefined })}
                    >
                        All ({stats?.total || 0})
                    </button>
                    <button
                        className={`pill ${filter.level === 'critical' ? 'highlight' : 'muted'}`}
                        onClick={() => setFilter({ ...filter, level: 'critical' })}
                    >
                        Critical ({stats?.byLevel?.critical || 0})
                    </button>
                    <button
                        className={`pill ${filter.level === 'error' ? 'highlight' : 'muted'}`}
                        onClick={() => setFilter({ ...filter, level: 'error' })}
                    >
                        Error ({stats?.byLevel?.error || 0})
                    </button>
                    <button
                        className={`pill ${filter.level === 'warning' ? 'highlight' : 'muted'}`}
                        onClick={() => setFilter({ ...filter, level: 'warning' })}
                    >
                        Warning ({stats?.byLevel?.warning || 0})
                    </button>
                    <div style={{ width: '1px', background: 'var(--border)', margin: '0 0.5rem' }} />
                    <button
                        className={`pill ${filter.resolved === false ? 'highlight' : 'muted'}`}
                        onClick={() => setFilter({ ...filter, resolved: filter.resolved === false ? undefined : false })}
                    >
                        Unresolved only
                    </button>
                </div>

                {loading ? (
                    <div className="loading-panel">
                        <div className="spinner"></div>
                        <p>Loading logs...</p>
                    </div>
                ) : logs.length === 0 ? (
                    <div className="empty-state">
                        <CheckCircle size={48} style={{ color: 'var(--success)' }} />
                        <p>No error logs found. System is running smoothly!</p>
                    </div>
                ) : (
                    <div className="logs-list" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {logs.map((log) => (
                            <article
                                key={log._id}
                                className="log-card"
                                style={{
                                    padding: '0.75rem 1rem',
                                    background: log.resolved ? 'var(--surface-muted)' : 'var(--surface)',
                                    borderRadius: 'var(--radius-md)',
                                    borderLeft: `4px solid ${levelColors[log.level]}`,
                                    opacity: log.resolved ? 0.6 : 1,
                                    cursor: 'pointer',
                                }}
                                onClick={() => setExpandedLog(expandedLog === log._id ? null : log._id)}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                        <span style={{ color: levelColors[log.level] }}>{levelIcons[log.level]}</span>
                                        <div>
                                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                                <span className={`pill ${log.level === 'critical' ? 'error' : log.level === 'error' ? 'warning' : 'muted'}`} style={{ fontSize: '0.65rem' }}>
                                                    {log.level.toUpperCase()}
                                                </span>
                                                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{log.source}</span>
                                            </div>
                                            <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem' }}>{log.message.substring(0, 100)}{log.message.length > 100 ? '...' : ''}</p>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <time style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>{formatDate(log.createdAt)}</time>
                                        {!log.resolved && (
                                            <button
                                                className="ghost-button"
                                                style={{ padding: '0.25rem' }}
                                                onClick={(e) => { e.stopPropagation(); handleResolve(log._id); }}
                                            >
                                                <CheckCircle size={16} />
                                            </button>
                                        )}
                                        <button
                                            className="ghost-button"
                                            style={{ padding: '0.25rem', color: 'var(--error)' }}
                                            onClick={(e) => { e.stopPropagation(); handleDelete(log._id); }}
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>

                                {expandedLog === log._id && (
                                    <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--surface-muted)', borderRadius: 'var(--radius-sm)', fontSize: '0.8rem' }}>
                                        <h4 style={{ margin: '0 0 0.5rem' }}>Full Message</h4>
                                        <p style={{ margin: '0 0 1rem', whiteSpace: 'pre-wrap' }}>{log.message}</p>

                                        {log.stack && (
                                            <>
                                                <h4 style={{ margin: '0 0 0.5rem' }}>Stack Trace</h4>
                                                <pre style={{ margin: 0, padding: '0.5rem', background: '#1a1a1a', borderRadius: 'var(--radius-sm)', overflow: 'auto', maxHeight: '200px', fontSize: '0.7rem' }}>
                                                    {log.stack}
                                                </pre>
                                            </>
                                        )}

                                        {log.metadata && (
                                            <>
                                                <h4 style={{ margin: '1rem 0 0.5rem' }}>Metadata</h4>
                                                <pre style={{ margin: 0, padding: '0.5rem', background: '#1a1a1a', borderRadius: 'var(--radius-sm)', overflow: 'auto', fontSize: '0.7rem' }}>
                                                    {JSON.stringify(log.metadata, null, 2)}
                                                </pre>
                                            </>
                                        )}

                                        {log.resolved && log.resolvedAt && (
                                            <p style={{ margin: '1rem 0 0', color: 'var(--success)' }}>
                                                <CheckCircle size={14} style={{ verticalAlign: 'middle', marginRight: '0.25rem' }} />
                                                Resolved on {formatDate(log.resolvedAt)}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </article>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
};
